import { createContext, useContext, useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import { useOrgAuthMethods } from 'api/AuthenticationPolicyAPI';
import {
  useOrganizationProjects,
  useOrganizationRoles,
  useOrganizationMembers,
  usePermissions,
} from 'api/OrganizationAPI';
import { UseRestQueryOptions } from 'utils/hooks/api';
import { Member, Permission, Project, Role } from '../types';
import { isMfaAvailable } from '../utils';

const defaultValue = {
  isLoading: false,
  isError: false,
  projects: [] as Project[],
  permissions: [] as Permission[],
  roles: [] as Role[],
  projectsById: {},
  rolesById: {},
  members: [] as Member[],
  membersById: {},
  isMfaAvailable: false,
  refetchAll: () => undefined,
};

type OrganizationMembersQueryContextValue = {
  isLoading: boolean;
  isError: boolean;
  projects: any[];
  projectsById: Record<string, any>;
  permissions: any[];
  roles: any[];
  rolesById: Record<string, any>;
  members: Member[];
  membersById: Record<string, Member>;
  isMfaAvailable: boolean;
  refetchAll: () => void;
};

export const OrganizationMembersQueryContext =
  createContext<OrganizationMembersQueryContextValue>(defaultValue);

const queryOptions = {
  // Limiting refetching while UI work is in progress
  refetchOnMount: true,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchInterval: false,
  refetchIntervalInBackground: false,
};

export const OrganizationMembersQueryContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    data: { projects = [] } = {},
    isLoading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
  } = useOrganizationProjects(undefined, queryOptions);
  const {
    data: { permissions = [] } = {},
    isLoading: permissionsLoading,
    error: permissionsError,
    refetch: refetchPermissions,
  } = usePermissions(undefined, queryOptions);
  const {
    data: { roles = [] } = {},
    isLoading: rolesLoading,
    error: rolesError,
    refetch: refetchRoles,
  } = useOrganizationRoles(undefined, queryOptions);
  const {
    data: { members = [] } = {},
    isLoading: membersLoading,
    error: membersError,
    refetch: refetchMembers,
  } = useOrganizationMembers(undefined, queryOptions);
  const {
    data: orgAuthMethods = {},
    isLoading: orgAuthMethodsLoading,
    error: orgAuthMethodsError,
    refetch: refetchOrgAuthMethods,
  } = useOrgAuthMethods(undefined, queryOptions as UseRestQueryOptions);

  const refetchAll = () => {
    refetchProjects();
    refetchPermissions();
    refetchRoles();
    refetchMembers();
  };

  const projectsById = useMemo(() => keyBy(projects, 'id'), [projects]);

  const rolesById = useMemo(() => keyBy(roles, 'id'), [roles]);

  const membersById = useMemo(() => keyBy(members, 'userId'), [members]);

  const value = {
    isLoading:
      projectsLoading ||
      permissionsLoading ||
      rolesLoading ||
      membersLoading ||
      orgAuthMethodsLoading,
    isError:
      !!projectsError ||
      !!permissionsError ||
      !!rolesError ||
      !!membersError ||
      !!orgAuthMethodsError,
    projects,
    projectsById,
    permissions,
    roles,
    rolesById,
    members,
    membersById,
    isMfaAvailable: isMfaAvailable(orgAuthMethods),
    refetchOrgAuthMethods,
    refetchAll,
  };

  return (
    <OrganizationMembersQueryContext.Provider value={value}>
      {children}
    </OrganizationMembersQueryContext.Provider>
  );
};

export const useOrganizationMembersQueryContext = () => useContext(OrganizationMembersQueryContext);
